// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AppIcon, IconButton } from '@loa/ui-components';
import { intelligenceApi, streamMessage } from '../../api/intelligenceApi';
import { suggestionsApi, type RelationshipSuggestion } from '../../api/suggestionsApi';
import type { RelationshipCashflow } from '../../api/relationshipCashflowApi';
import type { AdvisorPageContext } from '../../types/pageContext';
import { colors } from '@loa/design-tokens';

const VegaChart = lazy(() =>
  import('../funds/VegaChart').then((m) => ({ default: m.VegaChart })),
);

/**
 * The workspace's "AI editor" tab (cashflow-modelling-tab PRD §5.3, editor
 * redesign 2026-07): a deterministic intro bubble summarising the open
 * forecast, the chat thread with APPLIED-CHANGE cards (built from the
 * tool_call started/completed SSE events), reviewed what-if plans as
 * suggestion chips, and the "Describe a change…" composer streaming a
 * CASHFLOW_COPILOT session. Every message carries the CASHFLOW_WORKSPACE
 * page context (profileId + open scenarioId); when a scenario write
 * completes, {@code onScenarioChanged} fires so the chart + parameters
 * refetch. Degrades to a notice — never breaks the manual workspace — when
 * the intelligence service is unavailable (R22).
 */

/** The scenario-editor writes — completion of any of these means the forecast changed. */
export const SCENARIO_WRITE_TOOLS = new Set([
  'set_scenario_assumption',
  'add_scenario_income_or_spending',
  'add_scenario_account',
  'add_scenario_asset',
  'add_scenario_disposal',
  'add_scenario_sweep_surplus',
  'add_scenario_sweep_rule',
]);

interface AppliedChange {
  tool: string;
  line: string;
  applied: boolean;
}

interface ChatMsg {
  id: number;
  role: 'user' | 'assistant' | 'changes';
  content: string;
  changes?: AppliedChange[];
}

/** One mono card line per scenario write, from the tool_call_started args. */
export function changeLine(tool: string, input: unknown): string {
  const a = (input ?? {}) as Record<string, unknown>;
  const year = String(a.validFrom ?? '').slice(0, 4);
  const euro = (v: unknown) => `€${Number(v ?? 0).toLocaleString('en-IE')}`;
  switch (tool) {
    case 'add_scenario_income_or_spending':
      return `+ ${a.direction === 'EXPENDITURE' ? 'Spending' : 'Income'} · ${a.label ?? ''} ${euro(a.amount)} · ${year}`;
    case 'add_scenario_account':
      return `+ Account · ${a.label ?? a.accountType ?? ''} ${euro(a.balance)} · ${year}`;
    case 'add_scenario_asset':
      return `+ Asset · ${a.name ?? a.assetType ?? ''} ${euro(a.value)} · ${year}`;
    case 'add_scenario_disposal':
      return `− Sale · ${String(a.targetRef ?? '').split(':')[0]} · ${year}`;
    case 'add_scenario_sweep_surplus':
      return `~ Rule · sweep surplus → account · ${year}`;
    case 'add_scenario_sweep_rule':
      return `~ Rule · account sweep over ${euro(a.thresholdBalance)} · ${year}`;
    case 'set_scenario_assumption':
      return `~ Assumption · ${a.assumptionKey ?? ''} → ${a.value ?? ''}${year ? ` · ${year}` : ''}`;
    default:
      return `~ ${tool}`;
  }
}

/** Deterministic intro bubble for the open forecast (design: "I've loaded…"). */
export function introMessage(
  scenarioName: string | null,
  cashflow: RelationshipCashflow | null,
): string {
  if (!scenarioName) {
    return "You're viewing the base projection. Ask me anything about it — open or create a forecast to make changes.";
  }
  const parts: string[] = [];
  const year0 = cashflow?.years?.[0];
  if (year0) {
    parts.push(`a €${Number(year0.surplus).toLocaleString('en-IE')} annual surplus`);
  }
  const retire = cashflow?.events?.find((e) => e.type === 'RETIREMENT');
  if (retire) {
    parts.push(`retirement in year ${retire.year}`);
  }
  const inflation = cashflow?.assumptions?.priceInflationRate;
  if (inflation !== null && inflation !== undefined) {
    parts.push(`inflation held at ${inflation}%`);
  }
  const summary = parts.length > 0 ? ` It shows ${parts.join(', ')}.` : '';
  return `I've loaded the ${scenarioName} scenario.${summary} What would you like to explore?`;
}

interface Props {
  profileId: string;
  /** The open forecast; null in base mode (chat is read-only Q&A). */
  scenarioId: string | null;
  scenarioName: string | null;
  cashflow: RelationshipCashflow | null;
  onScenarioChanged: () => void;
}

/** Markdown renderers: fenced vega-lite blocks render as charts (rare — the
 *  copilot's prompt says the workspace chart is authoritative). */
const markdownComponents = {
  pre: ({ children }: { children?: React.ReactNode }) => {
    const child = children as { props?: { className?: string; children?: React.ReactNode } } | undefined;
    const className = child?.props?.className ?? '';
    const code = String(child?.props?.children ?? '');
    if (/language-(vega-lite|vegalite|vega)/.test(className)) {
      return (
        <Suspense fallback={<pre className="text-body-04">{code}</pre>}>
          <VegaChart specJson={code.trim()} />
        </Suspense>
      );
    }
    return <pre className="overflow-x-auto text-body-04 bg-lilac-06 rounded-lg p-sm">{children}</pre>;
  },
};

export function CashflowChatRail({ profileId, scenarioId, scenarioName, cashflow, onScenarioChanged }: Props) {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<RelationshipSuggestion[]>([]);
  const sessionRef = useRef<string | null>(null);
  const abortRef = useRef<(() => void) | null>(null);
  const nextId = useRef(1);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Suggestion chips: the relationship's active reviewed what-if plans (R20).
  useEffect(() => {
    suggestionsApi
      .list(profileId)
      .then((all) =>
        setSuggestions(all.filter((s) => s.status === 'SUGGESTED' && s.type === 'CASHFLOW_MODEL')),
      )
      .catch(() => setSuggestions([]));
  }, [profileId]);

  useEffect(() => () => abortRef.current?.(), []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const pageContext = useCallback(
    (): AdvisorPageContext => ({
      pageType: 'CASHFLOW_WORKSPACE',
      entityIds: { profileId, ...(scenarioId ? { scenarioId } : {}) },
      url: window.location.pathname,
      pageTitle: 'Cashflow Modelling',
    }),
    [profileId, scenarioId],
  );

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || streaming || unavailable) return;
      setError(null);
      setStreaming(true);
      setDraft('');
      const userId = nextId.current++;
      const assistantId = nextId.current++;
      setMessages((prev) => [
        ...prev,
        { id: userId, role: 'user', content },
        { id: assistantId, role: 'assistant', content: '' },
      ]);
      try {
        if (!sessionRef.current) {
          const session = await intelligenceApi.createSession('CASHFLOW_COPILOT', profileId);
          sessionRef.current = session.sessionId;
        }
      } catch {
        setUnavailable(true);
        setStreaming(false);
        setMessages((prev) => prev.filter((m) => m.id !== userId && m.id !== assistantId));
        return;
      }
      const appendToAssistant = (delta: string) =>
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + delta } : m)),
        );
      // Applied-change card: collect this turn's scenario writes from the
      // tool_call started/completed events and render them as one card.
      const turnChanges = new Map<string, AppliedChange>();
      const flushChanges = () => {
        const changes = [...turnChanges.values()];
        if (changes.length > 0) {
          const cardId = nextId.current++;
          setMessages((prev) => [...prev, { id: cardId, role: 'changes', content: '', changes }]);
        }
      };
      abortRef.current = streamMessage(
        sessionRef.current,
        content,
        {
          onToken: appendToAssistant,
          onToolCallStarted: (call) => {
            if (SCENARIO_WRITE_TOOLS.has(call.name)) {
              turnChanges.set(call.id, {
                tool: call.name,
                line: changeLine(call.name, call.input),
                applied: false,
              });
            }
          },
          onToolCallCompleted: (result) => {
            const change = turnChanges.get(result.id);
            if (change) {
              if (result.isError) {
                turnChanges.delete(result.id);
              } else {
                change.applied = true;
              }
            }
            if (result.name && !result.isError && SCENARIO_WRITE_TOOLS.has(result.name)) {
              // The forecast changed — the workspace refetches chart + parameters.
              onScenarioChanged();
            }
          },
          onDone: () => {
            flushChanges();
            setStreaming(false);
          },
          onError: (msg) => {
            flushChanges();
            setStreaming(false);
            setError(msg || 'The chat request failed.');
          },
        },
        undefined,
        pageContext(),
      );
    },
    [streaming, unavailable, profileId, pageContext, onScenarioChanged],
  );

  const applySuggestion = (s: RelationshipSuggestion) => {
    void send(
      `Apply this reviewed plan to the open forecast: ${s.title}.` +
        (s.body ? `\n${s.body}` : ''),
    );
  };

  return (
    <div data-testid="cashflow-chat-rail" className="flex flex-col flex-1 min-h-0 gap-sm">

      {unavailable && (
        <p data-testid="chat-rail-unavailable" className="text-body-04 text-grey-03 m-0">
          AI chat isn't enabled for your brokerage — the workspace stays fully editable manually.
        </p>
      )}

      {!unavailable && (
        <>
          <div ref={scrollRef} data-testid="chat-rail-messages" className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-sm">
            <div
              data-testid="chat-msg-intro"
              className="self-start max-w-full rounded-lg bg-lilac-06 px-sm py-sm text-body-03 text-grey-01"
            >
              {introMessage(scenarioName, cashflow)}
            </div>
            {messages.map((m) => {
              if (m.role === 'changes') {
                return (
                  <div
                    key={m.id}
                    data-testid="chat-msg-changes"
                    className="self-start w-full rounded-lg border border-lilac-04 bg-white px-sm py-sm flex flex-col gap-xs"
                  >
                    {m.changes?.map((c) => (
                      <p key={c.line} className="text-body-04 font-mono text-grey-01 m-0">{c.line}</p>
                    ))}
                    <p className="text-body-04 text-success-text m-0">✓ Applied</p>
                  </div>
                );
              }
              return (
                <div
                  key={m.id}
                  data-testid={`chat-msg-${m.role}`}
                  className={
                    m.role === 'user'
                      ? 'self-end max-w-full rounded-lg bg-purple-primary px-sm py-sm text-body-03 text-white'
                      : 'self-start max-w-full text-body-03 text-grey-01'
                  }
                >
                  {m.role === 'assistant' ? (
                    <ReactMarkdown components={markdownComponents}>
                      {m.content || (streaming ? '…' : '')}
                    </ReactMarkdown>
                  ) : (
                    m.content
                  )}
                </div>
              );
            })}
            {error && (
              <p data-testid="chat-rail-error" className="text-body-04 text-error-text m-0">{error}</p>
            )}
          </div>

          {suggestions.length > 0 && (
            <div data-testid="chat-rail-suggestions" className="flex flex-col gap-xs">
              <span className="text-body-04 text-grey-03">Suggested changes</span>
              {suggestions.slice(0, 3).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid="chat-suggestion-chip"
                  disabled={!scenarioId || streaming}
                  title={!scenarioId ? 'Open a forecast to apply suggestions' : undefined}
                  onClick={() => applySuggestion(s)}
                  className="text-left rounded-lg border border-lilac-04 bg-white px-sm py-xs text-body-04 text-purple-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {s.title}
                </button>
              ))}
            </div>
          )}

          {!scenarioId && (
            <p data-testid="chat-rail-base-hint" className="text-body-04 text-grey-03 m-0">
              Viewing the base projection — ask questions freely; open or create a forecast to make
              changes.
            </p>
          )}

          <div className="rounded-lg border border-grey-05 p-sm flex items-end gap-sm">
            <textarea
              data-testid="chat-rail-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(draft);
                }
              }}
              placeholder="Describe a change…"
              rows={2}
              className="flex-1 resize-none border-0 outline-none text-body-03 font-sans"
            />
            <IconButton
              variant="round-filled"
              size="sm"
              data-testid="chat-rail-send"
              aria-label="Send"
              disabled={streaming || draft.trim() === ''}
              onClick={() => void send(draft)}
              icon={<AppIcon name="arrow up" size={14} color={colors.white} />}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default CashflowChatRail;
